import { useRequiredContext } from "@redotech/react-util/context";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { SettingsContext } from "@redotech/redo-customer-portal-app/contexts/settings";
import { UserContext } from "@redotech/redo-customer-portal-app/contexts/user";
import { CustomizationContext } from "@redotech/redo-customer-portal-app/customization/customization-context";
import { useConfirmationCustomization } from "@redotech/redo-customer-portal-app/customization/use-confirmation-customization";
import { useCancelReturn } from "@redotech/redo-customer-portal-app/portal-four/hooks/use-cancel-return";
import {
  CancelReturnModal,
  isReturnCancellable,
} from "@redotech/redo-customer-portal-app/portal-four/shared-components/cancel-return-modal";
import {
  EditAddressModal,
  EditAddressModalData,
} from "@redotech/redo-customer-portal-app/portal-four/shared-components/edit-address-modal";
import { EditRefundMethodModal } from "@redotech/redo-customer-portal-app/portal-four/shared-components/edit-refund-method-modal";
import { PortalModal } from "@redotech/redo-customer-portal-app/portal-four/shared-components/portal-modal";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { useExchangeAddressLock } from "@redotech/redo-customer-portal-shared/hooks/useExchangeAddressLock/use-exchange-address-lock";
import { useReturnRefundDestination } from "@redotech/redo-customer-portal-shared/hooks/useRefundDestination/use-return-refund-destination";
import { useReturnConfirmation } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-return-confirmation";
import { resolveHideItemCosts } from "@redotech/redo-customer-portal-shared/hooks/useReview/use-summary-card-review";
import { fireReturnConfirmationLoadedEvent } from "@redotech/redo-customer-portal-shared/window-events/confirmation-loaded";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { ShipmentMethodName } from "@redotech/redo-return-core-common/draft-return/draft-return-enums";
import { CreationStatusError } from "@redotech/redo-return-core-common/draft-return/submit-draft-return";
import { isDropoffChosen } from "@redotech/redo-return-core-common/dropoff/utils";
import { closedReturnStatuses } from "@redotech/redo-return-core-common/return-status";
import { isOutstandingBalanceFullyWaived } from "@redotech/redo-return-core-common/returns/keep-fees-and-bonuses";
import { manualLabelInstructionLines } from "@redotech/redo-return-core-common/returns/manual-label-instructions";
import { createAddressAutocompleteForm } from "@redotech/redo-web/address-autocomplete-form";
import { InputSchema } from "@redotech/ui/form";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "usehooks-ts";
import { isConvenienceFeeMethod } from "../submission/components/is-convenience-fee";
import { PackagePickupModals } from "./components/package-pickup-modals";
import { resolveRefundMethodSummary } from "./components/resolve-refund-method-summary";
import { ReturnConfirmationDisplay } from "./confirmation-display/confirmation-display";
import { useUpdateReturnExchangeAddress } from "./hooks/use-update-return-exchange-address";

export function ReturnConfirmation() {
  const { t } = useTranslation();
  const returnId = useParams().returnId || "";
  const [searchParams] = useSearchParams();

  const settings = useContext(SettingsContext);
  const customizedSettings = useConfirmationCustomization();
  const user = useContext(UserContext);
  const rpcClient = useRequiredContext(RpcClientContext);

  const searchCarrierLocations = useCallback(
    async (params: { carrier: string; zip: string; country: string }) => {
      const { locations } = await rpcClient.getCarrierLocations(params);
      return locations;
    },
    [rpcClient],
  );
  const searchInStoreLocations = useCallback(
    async (params: { draftReturnId?: string; returnId?: string }) => {
      const { locations } = await rpcClient.getLocations(params);
      return locations ?? [];
    },
    [rpcClient],
  );
  const searchBlueYonderLocations = useCallback(
    async (params: { draftReturnId?: string; returnId?: string }) => {
      const { locations } = await rpcClient.getBlueYonderLocations(params);
      return locations;
    },
    [rpcClient],
  );
  const searchDropoffLocations = useCallback(
    async (params: { draftReturnId?: string; returnId?: string }) => {
      const { locations } = await rpcClient.getDropoffLocations(params);
      return locations;
    },
    [rpcClient],
  );
  const shouldAutoOpenBoxlessLocations = useMemo(() => {
    return searchParams.get("boxlessReturnsLocations") === "true";
  }, [searchParams]);
  const hasCreationStatusError = useMemo(
    () =>
      searchParams.get("creationStatusError") ===
      CreationStatusError.TEMPORAL_START_FAILED,
    [searchParams],
  );
  const navigate = useNavigate();

  const { customization } = useContext(CustomizationContext);

  const confirmation = useReturnConfirmation({
    settings: customizedSettings,
    manualLabelInstructions: manualLabelInstructionLines(
      customization?.pages?.confirmation?.steps?.manualLabelInstructions?.text,
    ),
    returnId,
    navigateToCheckout: (url: string) => {
      window.open(url, "_blank");
    },
    isMerchantCreated: user?.isMerchant ?? false,
    hasCreationStatusError,
  });

  const { returnHeader, showCreationStatus, returnData, loadingReturn } =
    confirmation;

  const hideItemCosts = resolveHideItemCosts(
    confirmation.returnType,
    customization?.pages?.confirmation?.behavior?.hideWarrantyItemCosts,
  );

  const shipmentMethod: ShipmentMethodName | undefined = (() => {
    if (!returnData) {
      return undefined;
    }
    if (isDropoffChosen(returnData)) {
      return ShipmentMethodName.BLUE_YONDER;
    }
    return undefined;
  })();

  const orderId = returnData?.orders?.[0]?.order;
  const cancelReturnMutation = useCancelReturn(orderId);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditAddressModal, setShowEditAddressModal] = useState(false);
  const updateAddressMutation = useUpdateReturnExchangeAddress(orderId);

  const [showEditRefundMethodModal, setShowEditRefundMethodModal] =
    useState(false);
  const refundDestination = useReturnRefundDestination({
    returnId,
    return_: returnData,
    settings,
    // Only returns that took a wallet rail have anything to show or edit.
    enabled: !!returnData?.thirdPartyRefund?.provider,
  });
  const refundMethod = resolveRefundMethodSummary({
    availableProviders: refundDestination.form.providers,
    destination: refundDestination.destination,
    onEditClick: () => setShowEditRefundMethodModal(true),
    refundTotal:
      confirmation.returnTotals?.refund.withOriginalOrderShipping ?? 0,
    returnStatus: returnData?.status,
  });

  const closeRefundMethodModal = () => {
    refundDestination.cancel();
    setShowEditRefundMethodModal(false);
  };

  const editAddressData:
    | EditAddressModalData
    | { showEditAddressLoading: true } = (() => {
    const address = returnData?.newOrderAddress ?? returnData?.shipping_address;
    if (!address) {
      return { showEditAddressLoading: true as const };
    }
    return {
      showEditAddressLoading: false,
      initialValues: {
        name: address.name ?? "",
        address1: address.address1,
        address2: address.address2 ?? null,
        city: address.city,
        province: address.province ?? null,
        zip: address.zip ?? "",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        countryCode: (address.country_code as RedoShopifyCountryCode) ?? "US",
        email: address.email ?? null,
        phone: address.phone ?? null,
        company: address.company ?? null,
      },
      texts: {
        shipItemsTo: t("Shipping new items to:"),
        title: t("Edit exchange address"),
      },
    };
  })();

  const handleEditAddressConfirm = async (
    formValues: InputSchema.Value<
      ReturnType<typeof createAddressAutocompleteForm>
    >,
  ) => {
    updateAddressMutation.mutate(
      {
        returnId,
        newOrderAddress: {
          name: formValues.name,
          address1: formValues.address1,
          address2: formValues.address2 ?? undefined,
          city: formValues.city,
          province: formValues.province ?? undefined,
          zip: formValues.zip,
          country_code: formValues.countryCode,
          email: formValues.email ?? undefined,
          phone: formValues.phone ?? undefined,
          company: formValues.company ?? undefined,
        },
      },
      { onSuccess: () => setShowEditAddressModal(false) },
    );
  };

  const handleCancelReturn = () => {
    cancelReturnMutation.mutate(returnId, {
      onSuccess: () => navigate("/returns-portal/orders"),
    });
  };

  const isExchangeReturn = !!(
    returnData?.advancedExchangeItems?.length ||
    returnData?.products.some((p) => p.exchange_for || p.exchangeGroupItem)
  );

  const shopifyOrderCreated = (returnData?.exchangeOrder?.length ?? 0) > 0;

  const outstandingBalanceManuallyWaived = isOutstandingBalanceFullyWaived({
    options: returnData?.options,
    processingInput: returnData?.processingInput,
  });

  const showEditAddressButton =
    (settings?.returns?.returnEditing?.allowCustomerEditExchangeAddress ??
      false) &&
    isExchangeReturn &&
    !!returnData?.status &&
    !closedReturnStatuses.includes(returnData.status) &&
    !shopifyOrderCreated;
  const exchangeAddressLockedValues = useExchangeAddressLock({
    returnId,
    enabled: showEditAddressButton,
  });

  const showCancelButton =
    (settings?.returns?.returnEditing?.allowCustomerCancelReturn ?? false) &&
    isReturnCancellable({
      status: returnData?.status,
      provision: returnData?.provision,
    });

  const screen = useScreenType();

  useDocumentTitle(returnHeader.title || t("Returns & Exchanges"));

  useEffect(() => {
    if (!showCreationStatus && returnId) {
      fireReturnConfirmationLoadedEvent(returnId);
    }
  }, [showCreationStatus, returnId]);

  return (
    <>
      <ReturnConfirmationDisplay
        chargesExchangeShippingViaStripe={
          confirmation.chargesExchangeShippingViaStripe
        }
        creationStatus={confirmation.creationStatus}
        currency={confirmation.currency}
        customSuccessMessage={confirmation.customSuccessMessage}
        deadlineDays={confirmation.deadlineDays}
        error={confirmation.error}
        errorCodeData={confirmation.errorCodeData}
        exchangeShippingName={confirmation.exchangeShippingName}
        hasCreationStatusError={hasCreationStatusError}
        hideItemCosts={hideItemCosts}
        isConvenienceFee={isConvenienceFeeMethod(shipmentMethod)}
        isLabelCovered={confirmation.isLabelCovered}
        labelDeducted={confirmation.labelDeducted}
        loadingReturn={loadingReturn}
        manualLabelData={confirmation.manualLabelData}
        manualLabelInfo={confirmation.manualLabelInfo}
        newItems={confirmation.newItems}
        newItemsHeader={confirmation.newItemsHeader}
        newOrderAddress={confirmation.newOrderAddress}
        onCancelClick={() => setShowCancelModal(true)}
        onEditAddressClick={() => setShowEditAddressModal(true)}
        onOpenUrl={(url) => window.open(url, "_blank")}
        originalOrderShippingAmount={
          returnData?.refundOriginalShipping
            ? (returnData.refundOriginalShippingAmount ?? 0)
            : 0
        }
        outstandingBalanceManuallyWaived={outstandingBalanceManuallyWaived}
        pickupModals={confirmation.pickupModals}
        products={confirmation.products}
        refundMethod={refundMethod}
        rejected={confirmation.rejected}
        rejectHtml={confirmation.rejectHtml}
        returnAddress={confirmation.returnAddress}
        returnHeader={returnHeader}
        returnId={returnId}
        returnItemsHeader={confirmation.returnItemsHeader}
        returnTotals={confirmation.returnTotals}
        returnType={confirmation.returnType}
        searchBlueYonderLocations={searchBlueYonderLocations}
        searchCarrierLocations={searchCarrierLocations}
        searchDropoffLocations={searchDropoffLocations}
        searchInStoreLocations={searchInStoreLocations}
        settings={settings}
        shouldAutoOpenBoxlessLocations={shouldAutoOpenBoxlessLocations}
        shouldShowSummary={confirmation.shouldShowSummary}
        showAddresses={confirmation.showAddresses}
        showCancelButton={showCancelButton}
        showCreationStatus={showCreationStatus}
        showDeadline={confirmation.showDeadline}
        showEditAddressButton={showEditAddressButton}
        showReturnSteps={confirmation.showReturnSteps}
        stepResource={confirmation.stepResource}
        steps={confirmation.steps}
        summaryFeesHeader={confirmation.summaryFeesHeader}
        summaryFeesOrderHeader={confirmation.summaryFeesOrderHeader}
        texts={confirmation.texts}
        upsellBillingTrigger={confirmation.upsellBillingTrigger}
        warnings={confirmation.warnings}
      />
      <PackagePickupModals pickupModals={confirmation.pickupModals} />
      {showCancelModal && (
        <CancelReturnModal
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelReturn}
        />
      )}
      {showEditRefundMethodModal && !!refundMethod && (
        <PortalModal
          isFullScreen={screen !== ScreenType.DESKTOP}
          isOpen
          onModalCloseRequested={closeRefundMethodModal}
        >
          <EditRefundMethodModal
            currency={confirmation.currency}
            form={refundDestination.form}
            onClose={closeRefundMethodModal}
            onSave={() => {
              void refundDestination.save().then((saved) => {
                if (saved) {
                  setShowEditRefundMethodModal(false);
                }
              });
            }}
            refundAmount={
              confirmation.returnTotals?.refund.withOriginalOrderShipping ?? 0
            }
            saveError={refundDestination.saveError}
            saving={refundDestination.saving}
            validationMessage={refundDestination.validationMessage}
          />
        </PortalModal>
      )}
      {showEditAddressModal && (
        <PortalModal
          isFullScreen={screen !== ScreenType.DESKTOP}
          isOpen
          onModalCloseRequested={() => setShowEditAddressModal(false)}
        >
          <EditAddressModal
            data={editAddressData}
            error={updateAddressMutation.error?.message}
            lockedValues={exchangeAddressLockedValues}
            onClose={() => setShowEditAddressModal(false)}
            onConfirm={handleEditAddressConfirm}
            saving={updateAddressMutation.isPending}
          />
        </PortalModal>
      )}
    </>
  );
}
